import { PointData } from 'data/FlyerData';
import { TextAlignment } from 'data/TextAlignment';
import { ColorResult } from 'react-color';
import { extractAlphaFromHex } from '.';

const PIXI = (window as any).PIXI;

export const colorizeSprite = (sprite: PIXI.Sprite, colorHex: string) => {
  const components = extractAlphaFromHex(colorHex);
  sprite.tint = PIXI.utils.string2hex(components[0]);
  sprite.alpha = components[1];
};

export const getHexFromColor = (color: ColorResult): string => {
  const toHex = (v: number): string => {
    let h = v.toString(16);
    if (h.length < 2) {
      h = '0' + h;
    }
    return h;
  };
  let hex = `#${toHex(color.rgb.r)}${toHex(color.rgb.g)}${toHex(color.rgb.b)}`;
  if (color.rgb.a !== undefined && color.rgb.a < 1) {
    hex += toHex(Math.round(color.rgb.a * 255));
  }
  return hex;
};

const getSpaceWidth = (style: PIXI.TextStyle): number => {
  // use 'm' char width instead?
  const wordWidth = PIXI.TextMetrics.measureText('m m', style).width;
  const charWidth = PIXI.TextMetrics.measureText('m', style).width;
  return wordWidth - 2 * charWidth;
};

export const splitTextIntoWords = (
  container: PIXI.Container,
  text: string,
  textStyle: PIXI.TextStyle,
  boundingBox: PIXI.Rectangle
) => {
  const metrics = PIXI.TextMetrics.measureText(text, textStyle);
  // console.log('Current text:');
  // console.log(metrics);
  const spaceWidth = getSpaceWidth(textStyle);
  // console.log('White space: ' + spaceWidth);
  // const ws = PIXI.TextMetrics.measureText('\u0020', frame.textStyle);
  // console.log(ws);
  const { lineHeight, lineWidths, height } = metrics;
  const lines = metrics.lines as any as string[];
  let y = 0;
  switch (textStyle.align) {
    case TextAlignment.Left:
      container.x = boundingBox.x;
      break;
    case TextAlignment.Center:
      container.x = boundingBox.x + boundingBox.width * 0.5;
      break;
    case TextAlignment.Right:
      container.x = boundingBox.right;
      break;
    default:
      break;
  }
  const verticalAlign = (textStyle as any).verticalAlign as TextAlignment;
  switch (verticalAlign) {
    case TextAlignment.Top:
      container.y = boundingBox.y;
      y = 0;
      break;
    case TextAlignment.Bottom:
      container.y = boundingBox.bottom;
      y = -height;
      break;
    case TextAlignment.Middle:
      container.y = boundingBox.y + boundingBox.height * 0.5;
      y = -height / 2;
      break;
    default:
      break;
  }
  for (var i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineWidth = lineWidths[i];
    const words = line.split(' ');
    let x = 0;

    switch (textStyle.align) {
      case TextAlignment.Center:
        x = -lineWidth / 2;
        break;
      case TextAlignment.Right:
        x = -lineWidth;
        break;
      default:
        break;
    }

    words.forEach(w => {
      const wordView = new PIXI.Text(w, {
        ...textStyle
        // fill: '#ff0000cc'
      });
      wordView.x = x;
      wordView.y = y;
      container.addChild(wordView);
      const wordWidth = PIXI.TextMetrics.measureText(w, textStyle).width;
      x += wordWidth + spaceWidth;
    });
    y += lineHeight;
  }
};

const appendRectangle = (base: PointData[], rectangle: PointData[]) => {
  // merge rectangle to the bottom, points are clockwise starting from top-left
  const bottomPointsIndex = base.length / 2;
  const bottomRight = base[bottomPointsIndex];
  const bottomLeft = base[bottomPointsIndex + 1];
  const baseWidth = bottomRight.x - bottomLeft.x;
  const newRectWidth = rectangle[1].x - rectangle[0].x;
  if (baseWidth === newRectWidth) {
    bottomRight.y = bottomLeft.y = rectangle[2].y;
  } else {
    if (newRectWidth > baseWidth) {
      // expands
      // move up bottom
      bottomRight.y = bottomLeft.y = rectangle[0].y;
    } else {
      // contract
      rectangle[0].y = rectangle[1].y = bottomRight.y;
    }
    // add new points
    base.splice(
      bottomPointsIndex + 1,
      0,
      rectangle[1],
      rectangle[2],
      rectangle[3],
      rectangle[0]
    );
  }
};

export const addTextBackground = (
  container: PIXI.Container,
  textStyle: PIXI.TextStyle,
  boundingBox: PIXI.Rectangle,
  metrics: PIXI.TextMetrics,
  color: string
) => {
  const spaceWidth = getSpaceWidth(textStyle);
  const paddingH = spaceWidth * 0.5;
  const { lineHeight, lineWidths, height } = metrics;
  const lines = metrics.lines as any as string[];
  if (!lines.length) {
    return;
  }
  const colorComponents = extractAlphaFromHex(color);
  const opaqueColor = PIXI.utils.string2hex(colorComponents[0]);
  const alpha = colorComponents[1];
  let y = 0;
  switch (textStyle.align) {
    case TextAlignment.Left:
      container.x = boundingBox.x;
      break;
    case TextAlignment.Center:
      container.x = boundingBox.x + boundingBox.width * 0.5;
      break;
    case TextAlignment.Right:
      container.x = boundingBox.right;
      break;
    default:
      break;
  }
  const verticalAlign = (textStyle as any).verticalAlign as TextAlignment;
  switch (verticalAlign) {
    case TextAlignment.Top:
      container.y = boundingBox.y;
      y = 0;
      break;
    case TextAlignment.Bottom:
      container.y = boundingBox.bottom;
      y = -height;
      break;
    case TextAlignment.Middle:
      container.y = boundingBox.y + boundingBox.height * 0.5;
      y = -height / 2;
      break;
    default:
      break;
  }
  const g = new PIXI.Graphics();
  g.beginFill(opaqueColor, alpha);

  const getX = (width: number): number => {
    switch (textStyle.align) {
      case TextAlignment.Center:
        return -width / 2;
      case TextAlignment.Right:
        return -width;
      default:
        return 0;
    }
  };
  let lineWidth = lineWidths[0];
  const lineRectHeight =
    textStyle.fontSize + metrics.fontProperties.descent + paddingH * 2;
  y += (lineHeight - (textStyle.fontSize as number)) / 2.5;
  let i = 0;
  if (lines.length === 1) {
    // draw simple rect
    g.drawRoundedRect(
      getX(lineWidth) - paddingH,
      y - paddingH,
      lineWidth + paddingH * 2,
      lineRectHeight,
      0
    );
  } else if (lineHeight === 0) {
    // draw longer line
    for (i = 1; i < lines.length; i++) {
      lineWidth = Math.max(lineWidth, lineWidths[i]);
    }
    g.drawRoundedRect(
      getX(lineWidth) - paddingH,
      y - paddingH,
      lineWidth + paddingH * 2,
      lineRectHeight,
      0
    );
  } else if (lineHeight > lineRectHeight) {
    // draw separate rectangles
    for (i = 0; i < lines.length; i++) {
      lineWidth = lineWidths[i];
      g.drawRoundedRect(
        getX(lineWidth) - paddingH,
        y - paddingH,
        lineWidth + paddingH * 2,
        lineRectHeight,
        0
      );
      y += lineHeight;
    }
  } else {
    // merge rectangles
    let x = getX(lineWidth);
    let base: PointData[] = [
      { x: x - paddingH, y: y - paddingH },
      { x: x + lineWidth + paddingH, y: y - paddingH },
      { x: x + lineWidth + paddingH, y: y - paddingH + lineRectHeight },
      { x: x - paddingH, y: y - paddingH + lineRectHeight }
    ];
    y += lineHeight;
    for (i = 1; i < lines.length; i++) {
      lineWidth = lineWidths[i];
      x = getX(lineWidth);
      const rectangle = [
        { x: x - paddingH, y: y - paddingH },
        { x: x + lineWidth + paddingH, y: y - paddingH },
        { x: x + lineWidth + paddingH, y: y - paddingH + lineRectHeight },
        { x: x - paddingH, y: y - paddingH + lineRectHeight }
      ];
      appendRectangle(base, rectangle);
      y += lineHeight;
    }

    g.moveTo(base[0].x, base[0].y);
    for (i = 1; i < base.length; i++) {
      const p = base[i];
      g.lineTo(p.x, p.y);
    }
    g.lineTo(base[0].x, base[0].y);
    g.endFill();
  }
  container.addChild(g);
};



// WEBPACK FOOTER //
// ./src/utils/PixiUtils.ts